import { createTheme } from '@mui/material/styles';

// Paper Dashboard colors extracted from _variables.scss
const paperDashboardTheme = createTheme({
  palette: {
    primary: {
      main: '#51cbce', // $primary-color
      light: '#7dd8da',
      dark: '#3a9ea0', // $primary-states-color (darken 10%)
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#66615B', // $default-color
      light: '#8a857f',
      dark: '#403D39', // $default-states-color
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#6bd098', // $success-color
      light: '#8ddcb0',
      dark: '#4fb87a', // $success-states-color (darken 10%)
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#51bcda', // $info-color
      light: '#7ccde4',
      dark: '#3a9fc0', // $info-states-color (darken 10%)
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#fbc658', // $warning-color
      light: '#fcd47f',
      dark: '#e5aa36', // $warning-states-color (darken 10%)
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#ef8157', // $danger-color
      light: '#f4a07e',
      dark: '#e05f2f', // $danger-states-color (darken 8%)
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#f4f3ef', // $default-body-bg
      paper: '#FFFFFF', // $white-bg
    },
    text: {
      primary: '#66615b', // $font-color
      secondary: '#9A9A9A', // $dark-gray
      disabled: '#a49e93', // $muted-color
    },
    divider: 'rgba(0,0,0,0.1)', // $hr-line
    grey: {
      100: '#F5F5F5', // $smoke-bg
      200: '#E3E3E3', // $light-gray
      300: '#DDDDDD', // $medium-gray
      400: '#ccc', // $table-line-color
      500: '#9A9A9A', // $dark-gray
      600: '#66615B', // $default-color
      700: '#444', // $light-black
      800: '#2c2c2c', // $black-color
      900: '#212120', // $dark-color
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', // MUI default font
    fontSize: 14, // $font-size-base
    fontWeightLight: 300, // $font-weight-light
    fontWeightRegular: 400, // $font-weight-normal
    fontWeightMedium: 600, // $font-weight-semi
    fontWeightBold: 700, // $font-weight-bold
    h1: {
      fontSize: '3.5em', // $font-size-h1
      fontWeight: 400,
    },
    h2: {
      fontSize: '2.5em', // $font-size-h2
      fontWeight: 400,
    },
    h3: {
      fontSize: '2em', // $font-size-h3
      fontWeight: 400,
    },
    h4: {
      fontSize: '1.714em', // $font-size-h4
      fontWeight: 400,
    },
    h5: {
      fontSize: '1.57em', // $font-size-h5
      fontWeight: 400,
    },
    h6: {
      fontSize: '1em', // $font-size-h6
      fontWeight: 600,
    },
    body1: {
      fontSize: '1em', // $font-paragraph
      lineHeight: 1.5, // $line-height-general
    },
    body2: {
      fontSize: '0.8571em', // $font-size-small
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.7142em', // $font-size-mini
    },
  },
  shape: {
    borderRadius: 4, // $border-radius-base
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '20px', // $border-radius-btn-base
          textTransform: 'uppercase',
          fontWeight: 500,
          padding: '11px 22px', // $padding-btn-vertical $padding-btn-horizontal
        },
        sizeSmall: {
          padding: '5px 15px', // $padding-small-vertical $padding-small-horizontal
          borderRadius: '26px', // $border-radius-btn-small
        },
        sizeLarge: {
          padding: '15px 48px', // $padding-large-vertical $padding-large-horizontal
          borderRadius: '50px', // $border-radius-btn-large
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px', // $border-radius-extreme
          boxShadow: '0 2px 2px rgba(204, 197, 185, 0.5)', // $box-shadow
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: '12px', // $border-radius-extreme
        },
        elevation1: {
          boxShadow: '0 2px 2px rgba(204, 197, 185, 0.5)', // $box-shadow
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px', // $border-radius-extreme
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px', // $border-radius-base
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #ccc', // $table-line-color
        },
        head: {
          fontWeight: 600, // $font-weight-semi
          color: '#66615b', // $font-color
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: 'rgba(107, 208, 152, 0.2)', // $light-green
        },
        standardError: {
          backgroundColor: 'rgba(239, 129, 87, 0.2)', // $light-red
        },
        standardWarning: {
          backgroundColor: 'rgba(251, 198, 88, 0.2)', // $light-orange
        },
        standardInfo: {
          backgroundColor: 'rgba(81, 188, 218, 0.2)', // $light-azure
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 2px rgba(204, 197, 185, 0.5)', // $box-shadow
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 2px 22px 0 rgba(0, 0, 0, 0.20), 0px 2px 30px 0 rgba(0, 0, 0, 0.35)', // $sidebar-box-shadow
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: '4px', // $border-radius-base
          fontSize: '12px', // $font-size-xs
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          borderRadius: '10px',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },
      styleOverrides: {
        select: {
          borderRadius: '4px', // $border-radius-base
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '10px 15px', // $padding-dropdown-vertical $padding-dropdown-horizontal
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: '4px', // $border-radius-base
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#51cbce', // $primary-color
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 500,
        },
      },
    },
  },
  transitions: {
    duration: {
      shortest: 150, // $fast-transition-time
      shorter: 200,
      short: 250,
      standard: 300, // $general-transition-time
      complex: 375, // $slow-transition-time
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
});

export default paperDashboardTheme;
